import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Icon, Spinner, Text } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import { FetchEmployeeIncidencesDocument } from 'generated/resources/graphql'
import isEmpty from 'lodash/isEmpty'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'
import PolicyForm from 'modules/timeoff/lib/TimeoffPolicy/Select'
import { employeeAllowanceIncidences } from 'modules/timeoff/store/collections/allowanceIncidences'
import allowances from 'modules/timeoff/store/collections/allowances'
import timeoffPolicies, {
  AllowanceMapItem,
  BulkAssignEmployeesDto,
} from 'modules/timeoff/store/collections/timeoffPolicies/policies'

interface Props {
  timeoffPolicyId: number
  employeeId: number
  onClose: () => void
}

const MoveEmployee = ({ timeoffPolicyId, employeeId, onClose }: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const [policySelected, isPolicySelected] = React.useState(false)
  const [selectedPolicyId, setSelectedPolicyId] = React.useState<number>(0)

  const [showSelectors, setShowSelectors] = React.useState(true)
  const [showAdjustmentLossAlert, setShowAdjustmentLossAlert] = React.useState<boolean>(false)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const form = React.useMemo(() => new PolicyForm({}), [])
  const { loading } = useAsyncData(() => allowances.fetch(), [])

  const policy = React.useMemo(() => timeoffPolicies.get(timeoffPolicyId), [timeoffPolicyId])
  const currentAllowances = policy?.allowances
  const employee = React.useMemo(() => employees.get(+employeeId), [employeeId])

  const { data, loading: loadingIncidences } = useQuery(FetchEmployeeIncidencesDocument, {
    variables: { employeeIds: [employeeId] },
  })
  const employeeIncidences = data?.timeoff.allowanceIncidences || []

  const allowancesWithIncidencesIds = Array.from(
    new Set(employeeIncidences.map((i) => i.timeoffAllowanceId))
  )

  const allowancesWithIncidences =
    currentAllowances!.filter((allowance) => {
      return allowancesWithIncidencesIds.includes(allowance.attributes.id)
    }) || []

  const hasAllowancesWithIncidences = !!allowancesWithIncidences.length

  const allowanceMappings: AllowanceMapItem[] = allowancesWithIncidences.map((a) => ({
    originalAllowanceId: a.id,
    newAllowanceId: 0,
    _destroy: false,
  }))

  const [bulkAssignEmployeeDto, setBulkAssignEmployeeDto] = React.useState<BulkAssignEmployeesDto>({
    employeeIds: [employeeId],
    mappings: allowanceMappings,
    policyId: selectedPolicyId,
  })

  const updateBulkAssignEmployeesDto = (data: BulkAssignEmployeesDto) => {
    setBulkAssignEmployeeDto(data)
  }

  const options = React.useMemo(
    () =>
      timeoffPolicies.models
        .filter((top) => top.attributes.id !== policy?.attributes.id)
        .map((top) => ({
          label: top.attributes.name,
          value: top.attributes.id,
        })),
    [policy]
  )

  const setMappingsDestroyValue = () => {
    bulkAssignEmployeeDto.mappings.forEach((mapping: AllowanceMapItem) => {
      if (mapping.newAllowanceId === 0) {
        return (mapping._destroy = true)
      }
    })
  }

  const onSelect = (field: Field) => {
    isPolicySelected(!isEmpty(field.value))
    setSelectedPolicyId(parseInt(field.value, 10))
    if (hasAllowancesWithIncidences) {
      setBulkAssignEmployeeDto({
        employeeIds: [employeeId],
        mappings: allowanceMappings,
        policyId: parseInt(field.value, 10),
      })
    }
  }

  const { running: moving, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (hasAllowancesWithIncidences) {
        setIsSaving(true)
        setMappingsDestroyValue()
        await timeoffPolicies.bulkAssignEmployees(bulkAssignEmployeeDto)
      } else {
        setIsSaving(true)
        const data = form.data()
        await employee?.save(data)
      }
      closePortal()
      navigate(navigation.settings.timeoffPolicies.id.employees.fullPath({ id: timeoffPolicyId }))
      window.location.reload()
    }
  )

  const allAllowancesSelected = (bulkAssignEmployeeDto: BulkAssignEmployeesDto) => {
    return bulkAssignEmployeeDto.mappings.every((mapping: AllowanceMapItem) => {
      return mapping.newAllowanceId !== 0
    })
  }

  const shouldShowAdjustmentLossAlert = () => {
    if (!allAllowancesSelected(bulkAssignEmployeeDto) && hasAllowancesWithIncidences) {
      setShowSelectors(false)
      return setShowAdjustmentLossAlert(true)
    } else {
      onSubmit(() => {})
    }
  }

  const keepAdjustments = () => {
    setShowAdjustmentLossAlert(false)
    return setShowSelectors(true)
  }

  const modalTitle = () => {
    if (showAdjustmentLossAlert) {
      return i18n.t('settings.timeoff_policies.adjustment_loss_modal.title')
    }
    return i18n.t('settings.timeoff_policies.move_employee_modal.title', {
      employee: `${employeeUser(employee!).attributes.first_name} ${
        employeeUser(employee!).attributes.last_name
      }`,
    })
  }

  const modalDescription = () => {
    if (showAdjustmentLossAlert) {
      return i18n.t('settings.timeoff_policies.adjustment_loss_modal.description')
    } else if (employee && !!employeeAllowanceIncidences(employee!).length && showSelectors) {
      return i18n.t('settings.timeoff_policies.move_employee_modal.description_complete')
    } else {
      return i18n.t('settings.timeoff_policies.move_employee_modal.description')
    }
  }

  return (
    <Modal
      illustration={showAdjustmentLossAlert ? 'alert' : 'document-edit'}
      title={modalTitle()}
      description={modalDescription()}
      onClose={onClose}
      footer={
        <>
          {showSelectors && (
            <Button onClick={shouldShowAdjustmentLossAlert} disabled={!policySelected || moving}>
              {i18n.t('common.submit')}
            </Button>
          )}
          {showAdjustmentLossAlert && (
            <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
              <Button type="positive" onClick={keepAdjustments} disabled={isSaving}>
                {i18n.t('settings.timeoff_policies.adjustment_loss_modal.primary_button')}
              </Button>
              <Button type="negative" disabled={isSaving} onClick={(event) => onSubmit(() => {})}>
                {i18n.t('settings.timeoff_policies.adjustment_loss_modal.secondary_button')}
              </Button>
            </Box>
          )}
        </>
      }
    >
      {loading || (loadingIncidences && <Spinner />)}
      {!loading && !loadingIncidences && showSelectors && (
        <Box>
          <FormRow>
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceEvenly"
              gap="s8"
              paddingTop="s24"
            >
              <Box minWidth="s240" paddingLeft="s4">
                <Text size="300" weight="medium" color="darker800">
                  {i18n.t('settings.timeoff_policies.manual_policy_mapper.current_policy')}
                </Text>
              </Box>
              <Box></Box>
              <Box width="s200">
                <Text size="300" weight="medium" color="darker800">
                  {i18n.t('settings.timeoff_policies.manual_policy_mapper.new_policy')}
                </Text>
              </Box>
            </Box>
          </FormRow>
          <FormRow>
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceEvenly"
              gap="s8"
              paddingBottom="s24"
            >
              <Box minWidth="s200">
                <Text size="300" weight="normal" color="darker800">
                  {policy!.attributes.name}
                </Text>
              </Box>
              <Box alignItems="center">
                <Icon.Medium icon="arrow-right" />
              </Box>
              <Box width="s200">
                <Select
                  none={i18n.t('common.none')}
                  field={form.get('timeoff_policy_id')}
                  onChange={(field) => onSelect(field)}
                  options={options}
                />
              </Box>
            </Box>
          </FormRow>
        </Box>
      )}
      {hasAllowancesWithIncidences && showSelectors && (
        <FormRow>
          <AllowanceMapper
            policyId={selectedPolicyId}
            currentAllowances={allowancesWithIncidences}
            setBulkAssignEmployeeDto={updateBulkAssignEmployeesDto}
            bulkAssignEmployeeDto={bulkAssignEmployeeDto}
          />
        </FormRow>
      )}
    </Modal>
  )
}

export default MoveEmployee
